import { JOB_ORDER_PROFILE_CONFIG, JOB_ORDER_TAB_IDS } from 'features/inventory-profiles/constants';
import { buildTabDataTableProps, getCommonTabs } from 'features/inventory-profiles/utils';
import strings from 'strings';
import CandidatesTab from 'UI/components/organisms/inventoryProfiles/CandidatesTab';
import HiringAuthorityTab from 'UI/components/organisms/inventoryProfiles/HiringAuthorityTab';
import JobOrderOverview from 'UI/components/organisms/inventoryProfiles/JobOrderOverview';
import { MatchMakingTab } from 'UI/components/organisms/inventoryProfiles/MatchMakingTab';
import PlacementsTab from 'UI/components/organisms/inventoryProfiles/PlacementsTab';
import SendoutsTab from 'UI/components/organisms/inventoryProfiles/SendoutsTab';
import SendoutsTabV2 from 'UI/components/organisms/inventoryProfiles/SendoutsTab/SendoutsTabV2';
import { TasksTab } from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { JobDescriptionViewer } from 'UI/components/organisms/inventoryProfiles/viewers';
import { EntityType } from 'UI/constants/entityTypes';

import { JOB_ORDER_TABS_CONFIG } from './JobOrderProfileV2.constants';

const COPIES = strings.inventoryProfiles;

/**
 * Tabs of the Job Order profile. Each tab gets only the props its V1 component reads, written one
 * by one with no spread. There is no Search Projects tab (V1 hides it).
 * @param {Object} params
 * @param {Object} params.jobOrder Job order from useJobOrderProfile, with its data sheet status
 * @param {string} params.jobOrderName `formattedJobOrder.header.title` (V1 `itemName`)
 * @param {Object} [params.dataSheet] Job order data sheet
 * @param {Object} [params.catalogs] Catalogs of useFetchCatalogs
 * @param {boolean} params.isOverviewLoading The profile, the data sheet or the catalogs are loading
 * @param {Object} params.permissions Result of useJobOrderPermissions
 * @param {string} params.sendoutDrawer `sendoutDrawer` of useJobOrderActions: the form, or the notice
 * @param {(name: string) => () => void} params.openDrawer From useProfileDrawer
 * @param {(name: string) => (item: { id: number|string }) => void} params.openItemDrawer From useProfileDrawer
 * @param {() => void} params.onWriteUpClick `openDataSheet` of useJobOrderActions
 * @param {({ sendoutId }: { sendoutId?: number|string }) => void} params.onNewPlacementClick `openNewPlacement` of useJobOrderActions
 * @param {() => void} params.refresh Reloads the profile silently
 * @param {(tabId: string) => void} params.refreshTab From useTabRefresh: remounts Placements after a
 *   save, which also resets PlacementsTab's one-shot refresh flag
 * @returns {Array<{ id: string, label: string, content: React.ReactNode, visible: boolean }>}
 */
export const buildJobOrderTabs = ({
  catalogs,
  dataSheet,
  isOverviewLoading,
  jobOrder,
  jobOrderName,
  onNewPlacementClick,
  onWriteUpClick,
  openDrawer,
  openItemDrawer,
  permissions,
  refresh,
  refreshTab,
  sendoutDrawer
}) => {
  const profileEndpoint = `${JOB_ORDER_PROFILE_CONFIG.endpoint}/${jobOrder.id}`;
  const {
    [JOB_ORDER_TAB_IDS.MatchMaking]: matchMaking,
    [JOB_ORDER_TAB_IDS.Sendouts]: sendouts,
    [JOB_ORDER_TAB_IDS.SendoutsV2]: sendoutsV2,
    [JOB_ORDER_TAB_IDS.Placements]: placements,
    [JOB_ORDER_TAB_IDS.Candidates]: candidates,
    [JOB_ORDER_TAB_IDS.HiringAuthorities]: hiringAuthorities,
    [JOB_ORDER_TAB_IDS.ActionPlan]: actionPlan
  } = JOB_ORDER_TABS_CONFIG;

  return [
    {
      id: JOB_ORDER_TAB_IDS.Overview,
      label: COPIES.common.tabs.overview,
      visible: true,
      content: (
        <JobOrderOverview
          catalogs={catalogs}
          dataSheet={dataSheet}
          loading={isOverviewLoading}
          onWriteUpClick={onWriteUpClick}
          profile={jobOrder}
        />
      )
    },
    {
      id: JOB_ORDER_TAB_IDS.JobDescription,
      label: COPIES.common.tabs.jobDescription,
      visible: permissions.canSeeJobDescription,
      content: <JobDescriptionViewer jobOrderId={jobOrder.id} />
    },
    {
      id: JOB_ORDER_TAB_IDS.MatchMaking,
      label: COPIES.common.tabs.matchMaking,
      visible: true,
      content: (
        <MatchMakingTab
          availableEntityTypesToMatch={matchMaking.availableEntityTypesToMatch}
          DataTableProps={matchMaking.DataTableProps}
          profileId={jobOrder.id}
        />
      )
    },
    {
      id: JOB_ORDER_TAB_IDS.Sendouts,
      label: COPIES.common.tabs.sendouts,
      visible: permissions.canUseSendouts,
      content: (
        <SendoutsTab
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: sendouts })}
          extraRenderers={sendouts.extraRenderers}
          initialColumns={sendouts.initialColumns}
          onNewItemClick={openDrawer(sendoutDrawer)}
          onSendoutClick={openItemDrawer(sendoutDrawer)}
          profile={jobOrder}
          profileModule={EntityType.Joborder}
          tablePreferencesKey={sendouts.tablePreferencesKey}
        />
      )
    },
    {
      id: JOB_ORDER_TAB_IDS.SendoutsV2,
      label: COPIES.common.tabs.sendoutsV2,
      visible: permissions.canSeeSendoutsV2,
      content: (
        <SendoutsTabV2
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: sendoutsV2 })}
          extraRenderers={sendoutsV2.extraRenderers}
          initialColumns={sendoutsV2.initialColumns}
          onNewItemClick={openDrawer(sendoutDrawer)}
          profile={jobOrder}
          profileModule={EntityType.Joborder}
          tablePreferencesKey={sendoutsV2.tablePreferencesKey}
        />
      )
    },
    {
      id: JOB_ORDER_TAB_IDS.Placements,
      label: COPIES.common.tabs.placements,
      visible: permissions.canSeePlacements,
      content: (
        <PlacementsTab
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: placements })}
          initialColumns={placements.initialColumns}
          onNewPlacementClick={onNewPlacementClick}
          onTriggerTabRefresh={refreshTab}
          profileId={jobOrder.id}
          profileModule={EntityType.Joborder}
          tablePreferencesKey={placements.tablePreferencesKey}
        />
      )
    },
    {
      id: JOB_ORDER_TAB_IDS.Candidates,
      label: COPIES.common.tabs.candidates,
      visible: true,
      content: (
        <CandidatesTab
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: candidates })}
          endpoint={profileEndpoint}
          extraRenderers={candidates.extraRenderers}
          initialColumns={candidates.initialColumns}
          joborderId={jobOrder.id}
          profileName={jobOrderName}
          tablePreferencesKey={candidates.tablePreferencesKey}
        />
      )
    },
    {
      id: JOB_ORDER_TAB_IDS.HiringAuthorities,
      label: COPIES.common.tabs.hiringAuthorities,
      visible: true,
      content: (
        <HiringAuthorityTab
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: hiringAuthorities })}
          initialColumns={hiringAuthorities.initialColumns}
          profile={jobOrder}
          search={false}
          tablePreferencesKey={hiringAuthorities.tablePreferencesKey}
        />
      )
    },
    {
      id: JOB_ORDER_TAB_IDS.ActionPlan,
      label: COPIES.sections.tabs.actionPlan.tabLabel,
      visible: permissions.canUseDailyPlan,
      content: (
        <TasksTab
          allowedTaskTypes={actionPlan.allowedTaskTypes}
          DataTableProps={actionPlan.DataTableProps}
          entityType={actionPlan.entityType}
          entityTypeId={actionPlan.entityTypeId}
          extraRenderers={actionPlan.extraRenderers}
          initialColumns={actionPlan.initialColumns}
          itemName={jobOrderName}
          profileId={jobOrder.id}
          tablePreferencesKey={actionPlan.tablePreferencesKey}
        />
      )
    },
    ...getCommonTabs({
      profileId: jobOrder.id,
      entity: JOB_ORDER_PROFILE_CONFIG,
      itemName: jobOrderName,
      onActivityComplete: refresh
    })
  ];
};
